/**
 * @fileoverview WebSocket message schemas for real-time communication
 * Enhanced with dashboard identification and chart command support
 */

import { z } from '@hono/zod-openapi';

export const WebSocketConnectionSchema = z.object({
  userId: z.string().min(1, 'User ID is required').openapi({
    description: 'Unique identifier for the user',
    example: 'user-123'
  }),
  websocketId: z.string().length(5, 'WebSocket ID must be exactly 5 digits').regex(/^\d+$/, 'WebSocket ID must be numeric').openapi({
    description: 'Unique 5-digit WebSocket identifier',
    example: '12345'
  }),
  clientType: z.enum(['dashboard', 'general']).openapi({
    description: 'Type of client connection',
    example: 'dashboard'
  }),
  connectionId: z.string().min(1, 'Connection ID is required').openapi({
    description: 'Current WebSocket connection identifier',
    example: 'conn-abc123'
  }),
  status: z.enum(['connected', 'disconnected']).default('connected').openapi({
    description: 'Current connection status',
    example: 'connected',
    default: 'connected'
  }),
  connectedAt: z.string().datetime('Invalid connection timestamp').openapi({
    description: 'ISO timestamp when connection was established',
    example: '2024-01-15T10:30:00Z'
  }),
  lastPing: z.string().datetime('Invalid last ping timestamp').optional().openapi({
    description: 'ISO timestamp of last ping received',
    example: '2024-01-15T10:35:00Z'
  }),
  disconnectedAt: z.string().datetime('Invalid disconnection timestamp').optional().openapi({
    description: 'ISO timestamp when connection was closed',
    example: '2024-01-15T10:40:00Z'
  }),
  subscriptions: z.array(z.string()).default(['global']).openapi({
    description: 'Array of subscription channels',
    example: ['global', 'user-updates'],
    default: ['global']
  }),
  token: z.string().optional().openapi({
    description: 'Authentication token',
    example: 'jwt-token-here'
  })
}).openapi({
  description: 'WebSocket connection data for persistent connections',
  example: {
    userId: 'user-123',
    websocketId: '12345',
    clientType: 'dashboard',
    connectionId: 'conn-abc123',
    status: 'connected',
    connectedAt: '2024-01-15T10:30:00Z',
    subscriptions: ['global', 'user-updates']
  }
});

export const RegisterWebSocketConnectionSchema = z.object({
  userId: z.string().min(1, 'User ID is required').openapi({
    description: 'Unique identifier for the user',
    example: 'user-123'
  }),
  connectionId: z.string().min(1, 'Connection ID is required').openapi({
    description: 'WebSocket connection identifier',
    example: 'conn-abc123'
  }),
  clientType: z.enum(['dashboard', 'general']).openapi({
    description: 'Type of client connection',
    example: 'dashboard'
  }),
  websocketId: z.string().length(5, 'WebSocket ID must be exactly 5 digits').regex(/^\d+$/, 'WebSocket ID must be numeric').optional().openapi({
    description: 'Optional 5-digit WebSocket ID for reconnection',
    example: '12345'
  }),
  token: z.string().optional().openapi({
    description: 'Authentication token',
    example: 'jwt-token-here'
  }),
  subscriptions: z.array(z.string()).default(['global']).optional().openapi({
    description: 'Array of subscription channels',
    example: ['global', 'user-updates'],
    default: ['global']
  })
}).openapi({
  description: 'Schema for registering new WebSocket connections',
  example: {
    userId: 'user-123',
    connectionId: 'conn-abc123',
    clientType: 'dashboard',
    token: 'jwt-token-here'
  }
});

export const UpdateWebSocketConnectionSchema = z.object({
  connectionId: z.string().min(1).optional().openapi({
    description: 'Updated connection ID for reconnection',
    example: 'conn-def456'
  }),
  status: z.enum(['connected', 'disconnected']).optional().openapi({
    description: 'Updated connection status',
    example: 'connected'
  }),
  lastPing: z.string().datetime().optional().openapi({
    description: 'Updated last ping timestamp',
    example: '2024-01-15T10:35:00Z'
  }),
  disconnectedAt: z.string().datetime().optional().openapi({
    description: 'Disconnection timestamp',
    example: '2024-01-15T10:40:00Z'
  }),
  subscriptions: z.array(z.string()).optional().openapi({
    description: 'Updated subscription channels',
    example: ['global', 'notifications']
  }),
  token: z.string().optional().openapi({
    description: 'Updated authentication token',
    example: 'new-jwt-token'
  })
}).openapi({
  description: 'Schema for updating WebSocket connections (reconnections, status changes)',
  example: {
    connectionId: 'conn-def456',
    status: 'connected',
    lastPing: '2024-01-15T10:35:00Z'
  }
});

export const WebSocketMessageSchema = z.object({
  type: z.enum([
    'ping',
    'pong',
    'subscribe',
    'broadcast',
    'authenticate',
    'authenticated',
    'identify_dashboard',
    'dashboard_id',
    'chart_command',
    'error',
    'connected',
    'subscribed',
  ]).openapi({
    description: 'Type of WebSocket message',
    example: 'broadcast'
  }),
  data: z.any().optional().openapi({
    description: 'Optional data payload for the message',
    example: { key: 'value' }
  }),
  token: z.string().optional().openapi({
    description: 'Authentication token',
    example: 'jwt-token-here'
  }),
  userId: z.string().optional().openapi({
    description: 'User identifier',
    example: 'user-123'
  }),
  websocketId: z.string().optional().openapi({
    description: 'WebSocket connection identifier',
    example: '12345'
  }),
  clientType: z.enum(['dashboard', 'general']).optional().openapi({
    description: 'Type of client connection',
    example: 'dashboard'
  }),
  channels: z.array(z.string()).optional().openapi({
    description: 'Array of channels for subscription',
    example: ['global', 'notifications']
  }),
  message: z.string().optional().openapi({
    description: 'Text message content',
    example: 'Hello, WebSocket!'
  }),
  timestamp: z.string().datetime().optional().openapi({
    description: 'ISO timestamp of the message',
    example: '2024-01-15T10:30:00Z'
  }),
  connectionId: z.string().optional().openapi({
    description: 'Connection identifier',
    example: 'conn-abc123'
  }),
  from: z.string().optional().openapi({
    description: 'Sender identifier',
    example: 'user-456'
  }),
  channel: z.string().optional().openapi({
    description: 'Target channel for the message',
    example: 'global'
  }),
  command: z.object({
    elementId: z.string().min(1).openapi({
      description: 'DOM element identifier',
      example: 'chart-1'
    }),
    action: z.enum(['scale', 'move', 'reset', 'center', 'minimize']).openapi({
      description: 'Action to perform on the element',
      example: 'scale'
    }),
    dimensions: z.object({
      width: z.string().min(1).openapi({
        description: 'Element width',
        example: '400px'
      }),
      height: z.string().min(1).openapi({
        description: 'Element height',
        example: '300px'
      })
    }).openapi({
      description: 'Element dimensions'
    }),
    position: z.object({
      top: z.string().min(1).openapi({
        description: 'Top position',
        example: '100px'
      }),
      left: z.string().min(1).openapi({
        description: 'Left position',
        example: '200px'
      })
    }).openapi({
      description: 'Element position'
    }),
    zIndex: z.number().int().min(0).openapi({
      description: 'Z-index for layering',
      example: 100
    })
  }).optional().openapi({
    description: 'Chart command object for dashboard interactions'
  })
}).openapi({
  description: 'Enhanced WebSocket message schema',
  example: {
    type: 'broadcast',
    data: { notification: 'New item added' },
    userId: 'user-123',
    websocketId: '12345',
    timestamp: '2024-01-15T10:30:00Z'
  }
});

export const DashboardIdResponseSchema = z.object({
  type: z.literal('dashboard_id').openapi({
    description: 'Message type for dashboard ID response',
    example: 'dashboard_id'
  }),
  websocketId: z.string().length(5).regex(/^\d+$/).openapi({
    description: 'Unique 5-digit WebSocket identifier',
    example: '12345'
  }),
  timestamp: z.string().datetime().openapi({
    description: 'ISO timestamp when response was sent',
    example: '2024-01-15T10:30:00Z'
  })
}).openapi({
  description: 'Dashboard ID response schema',
  example: {
    type: 'dashboard_id',
    websocketId: '12345',
    timestamp: '2024-01-15T10:30:00Z'
  }
});

export const ChartCommandMessageSchema = z.object({
  type: z.literal('chart_command').openapi({
    description: 'Message type for chart commands',
    example: 'chart_command'
  }),
  command: z.object({
    elementId: z.string().min(1, 'Element ID is required').openapi({
      description: 'DOM element identifier for the chart',
      example: 'chart-1'
    }),
    action: z.enum(['scale', 'move', 'reset', 'center', 'minimize']).openapi({
      description: 'Action to perform on the chart element',
      example: 'scale'
    }),
    dimensions: z.object({
      width: z.string().min(1, 'Width is required').openapi({
        description: 'Element width with units',
        example: '400px'
      }),
      height: z.string().min(1, 'Height is required').openapi({
        description: 'Element height with units',
        example: '300px'
      })
    }).openapi({
      description: 'Chart element dimensions'
    }),
    position: z.object({
      top: z.string().min(1, 'Top position is required').openapi({
        description: 'Top position with units',
        example: '100px'
      }),
      left: z.string().min(1, 'Left position is required').openapi({
        description: 'Left position with units',
        example: '200px'
      })
    }).openapi({
      description: 'Chart element position'
    }),
    zIndex: z.number().int().min(0, 'Z-index must be non-negative').openapi({
      description: 'Z-index for element layering',
      example: 100
    })
  }).openapi({
    description: 'Chart command details'
  }),
  websocketId: z.string().length(5).regex(/^\d+$/).optional().openapi({
    description: 'WebSocket identifier for targeting specific dashboard',
    example: '12345'
  }),
  timestamp: z.string().datetime().optional().openapi({
    description: 'ISO timestamp when command was issued',
    example: '2024-01-15T10:30:00Z'
  }),
  source: z.string().optional().openapi({
    description: 'Source of the command',
    example: 'dashboard-control'
  })
}).openapi({
  description: 'Chart command message schema for dashboard interactions',
  example: {
    type: 'chart_command',
    command: {
      elementId: 'chart-1',
      action: 'scale',
      dimensions: { width: '400px', height: '300px' },
      position: { top: '100px', left: '200px' },
      zIndex: 100
    },
    websocketId: '12345',
    timestamp: '2024-01-15T10:30:00Z'
  }
});

export const NotificationSchema = z.object({
  id: z.string().openapi({
    description: 'Unique notification identifier',
    example: 'notif-abc123'
  }),
  type: z.enum(['issue_created', 'issue_updated', 'issue_deleted', 'user_joined', 'system']).openapi({
    description: 'Type of notification',
    example: 'issue_created'
  }),
  data: z.any().openapi({
    description: 'Notification payload data',
    example: { issueId: 'issue-123', title: 'New Newsletter Issue' }
  }),
  timestamp: z.string().datetime().openapi({
    description: 'ISO timestamp when notification was created',
    example: '2024-01-15T10:30:00Z'
  }),
  userId: z.string().uuid().optional().openapi({
    description: 'Optional user ID for user-specific notifications',
    example: '550e8400-e29b-41d4-a716-446655440000'
  }),
}).openapi({
  description: 'Notification schema for real-time updates',
  example: {
    id: 'notif-abc123',
    type: 'issue_created',
    data: { issueId: 'issue-123', title: 'New Newsletter Issue' },
    timestamp: '2024-01-15T10:30:00Z',
    userId: '550e8400-e29b-41d4-a716-446655440000'
  }
});
